<template>
  <div id="select-country" class="add-bank">
    <form @submit.prevent="handleBankType">
      <h2 class="add-bank__title">Select currency</h2>
      <p class="add-bank__desc">
        Withdrawals are paid out in NGN, KES or USD. For a USD account outside the
        US, choose Other currencies or countries.
      </p>
      <label for="country" class="add-bank__label">Currency</label>
      <select
        name="country"
        id="country"
        class="add-bank__select"
        v-model="country"
        @input="setCountry"
        required
      >
        <option value="" disabled>Select currency</option>
        <option
          :value="country"
          v-for="(country, index) in countryOptions"
          :key="index"
        >
          {{ country }}
        </option>
      </select>
      <div v-if="linkToken">
        <PlaidLink
          clientName="GetEquity"
          :env="plaidEnv"
          :link_token="linkToken"
          :products="['auth', 'transactions']"
          :onSuccess="onPlaidSuccess"
          :onExit="onPlaidExit"
          :onEvent="onPlaidEvent"
          :onLoad="onPlaidLoad"
        >
          <div class="tw-mt-3" v-if="showPlaidButton">
            <p
              class="tw-text-[medium] tw-leading-[125%] tw-text-[#234160] tw-inline-flex"
            >
              If you're not redirected to Plaid,
              <span ref="plaidLink" class="tw-text-primary tw-cursor-pointer"
                >click to proceed</span
              >
            </p>
          </div>
        </PlaidLink>
      </div>
    </form>
    <div v-if="country === 'European Countries'" class="tw-mt-8">
      <label for="select country" class="tw-text-sm">
        Select European Country
      </label>
      <select
        name="syndicateType"
        id="syndicateType"
        class="tw-w-full tw-flex tw-flex-col tw-rounded tw-p-4"
        v-model="euroCountry"
        @input="setEuroCountry"
        required
      >
        <option value="" disabled>Select Country</option>
        <option
          :value="country.iso2"
          v-for="(country, index) in europeanCountries.countries"
          :key="index"
        >
          {{ country.name }}
        </option>
      </select>
    </div>
    <div v-if="euroCountry !== ''" class="tw-mb-32 tw-mt-10">
      <label for="select country" class="tw-text-sm">Select Your Bank</label>
      <div class="tw-relative tw-border tw-border-gray tw-rounded tw-px-4">
        <EuropeBankList
          class="tw-mt-8"
          placeHolder="Search"
          label="search"
          :search="bank"
          @set="setSearch"
        />
        <ul class="tw-h-72 tw-overflow-auto">
          <li
            v-for="(bank, index) in filteredBanks"
            :key="index"
            class="tw-flex tw-items-center tw-text-gray tw-text-lg tw-cursor-pointer tw-px-6 tw-my-4"
            @click="fetchNordigenLink(bank.id, bank.name)"
          >
            <img :src="bank.logo" class="tw-w-10 tw-mr-4" alt="bank logo" />
            {{ bank.name }}
          </li>
        </ul>
        <SmallLoader
          v-if="euroCountry !== '' && !euroBankLoading"
          class="tw-absolute tw-top-36 tw-left-40"
        />
        <EmptyState
          v-if="filteredBanks.length === 0 && euroBankLoading"
          class="tw-absolute tw-top-36 tw-left-32"
        >
          <template v-slot:message>
            <p class="message tw-text-center tw-text-gray tw-my-3">
              No banks found.
            </p>
          </template>
        </EmptyState>
      </div>
    </div>
    <ManualAddBank v-if="showManualForm" @added="(currency) => $emit('bankConnected', 'selectCountryForm', currency)" />
    <FlutterwaveAddBank 
      v-if="showFlutterwaveForm"
      :key="flutterwaveCurrency"
      :currency="flutterwaveCurrency" 
      @added="(currency) => $emit('bankConnected', 'selectCountryForm', currency)" 
    />
  </div>
</template>

<script>
  import FlutterwaveAddBank from "@/components/account/FlutterwaveAddBank";
  import ManualAddBank from "@/components/account/ManualAddBank";
import EmptyState from "@/components/notification/EmptyState";
import SmallLoader from "@/components/notification/SmallLoader";
import EuropeBankList from "@/components/wallet/EuropeBankList";
import bankApi from "@/utils/addBankApi";
import europeanCountries from "@/utils/json/europeanCountries.json";
import { setItem } from "@/utils/storage.js";
import MonoConnect from "@mono.co/connect.js";
import PlaidLink from "vue-plaid-link2";
import { mapActions } from "vuex";

  export default {
    name: "SelectCountryForm",

    components: {
      EuropeBankList,
      SmallLoader,
      EmptyState,
      PlaidLink,
      ManualAddBank,
      FlutterwaveAddBank,
    },

    data() {
      return {
        europeanCountries,
        country: "",
        euroCountry: "",
        allBanks: [],
        bank: "",
        plaidEnv: process.env.VUE_APP_PLAID_ENV,
        countryOptions: [
          // "Nigerian Naira (NGN)", // Mono
          "Nigerian Naira (NGN)",
          "Kenyan Shillings (KES)",
          // "Ghana",
          // "South Africa",
          // "European Countries",
          "United States Dollars (USD)",
          "Other currencies or countries",
        ],
        monoConnect: null,
        euroBankLoading: false,
        linkToken: "",
        plaidSuccess: false,
        showPlaidButton: false,
        showManualForm: false,
        showFlutterwaveForm: false,
        flutterwaveCurrency: "NGN",
      };
    },

    watch: {
      linkToken(val) {
        if (val.length > 0) {
          this.setLoading(true);
          this.showPlaidButton = true;
          setTimeout(() => {
            this.setLoading(false);
            this.$refs.plaidLink.click();
          }, 1000);
        }
      },
      country(val) {
        if (val !== "Other currencies or countries") {
          this.showManualForm = false;
        }
        const flutterwaveCurrencies = ["Nigerian Naira (NGN)", "Kenyan Shillings (KES)", "United States Dollars (USD)"];
        if (!flutterwaveCurrencies.includes(val)) {
          this.showFlutterwaveForm = false;
        }
      },
    },
    computed: {
      filteredBanks() {
        if (!this.bank) {
          return this.allBanks;
        }
        return this.allBanks.filter((bank) =>
          bank.name.toLowerCase().includes(this.bank.toLowerCase())
        );
      },
    },
    emits: ["bank-connected"],

    mounted() {},

    methods: {
      ...mapActions("Account", ["fetchBankAccounts"]),
      ...mapActions(["showToast", "setLoading"]),

      setSearch(text) {
        this.bank = text;
      },

      setCountry(event) {
        this.euroCountry = "";
        this.country = event.target.value;
        this.handleBankType();
      },
      async handleBankType() {
        switch (this.country) {
          // case "Nigerian Naira (NGN)": // Mono
          //   this.monoConnect = await this.setupMono("ng");
          //   this.monoConnect.open();
          //   break;
          case "Nigerian Naira (NGN)":
            this.flutterwaveCurrency = "NGN";
            this.showFlutterwaveForm = true;
            break;
          case "Kenyan Shillings (KES)":
            // this.monoConnect = await this.setupMono("ke");
            // this.monoConnect.open();
            this.flutterwaveCurrency = "KES";
            this.showFlutterwaveForm = true;
            break;
          case "Ghana":
            this.monoConnect = await this.setupMono("gh");
            this.monoConnect.open();
            break;
          case "South Africa":
            this.connectForeignAccount("stitch");
            break;
          case "United States Dollars (USD)":
            // this.connectForeignAccount("plaid");
            this.flutterwaveCurrency = "USD";
            this.showFlutterwaveForm = true;
            break;
          case "Other currencies or countries":
            this.showManualForm = true;
            break;
        }
      },
      async fetchNordigenLink(id, name) {
        this.setLoading(true);
        let redirectUri = `${window.location.origin}${window.location.pathname}`;
        try {
          const response = await bankApi.initiateBankConnection("nordigen", {
            id,
            name,
            redirectUri,
          });
          if (response.status === 201 || response.status === 200) {
            window.location = response.data.data.link;
          } else {
            this.setLoading(false);
            this.showToast({
              description: response.data.message,
              display: true,
              type: "error",
            });
          }
        } catch (error) {
          this.setLoading(false);
          this.showToast({
            description: "We could not initiate this integration.",
            display: true,
            type: "error",
          });
        }
      },

      async setEuroCountry(event) {
        this.euroCountry = event.target.value;
        this.euroBankLoading = false;
        try {
          const response = await bankApi.fetchInstitutions(this.euroCountry);
          if (response.status === 201 || response.status === 200) {
            this.allBanks = response.data.data;
            this.euroBankLoading = true;
          } else {
            this.showToast({
              description: response.data.message,
              display: true,
              type: "error",
            });
          }
        } catch (error) {
          this.showToast({
            description: "Error fetching institutions",
            display: true,
            type: "error",
          });
        }
      },

      async connectForeignAccount(type) {
        this.setLoading(true);
        let request = null;
        if (type === "stitch") {
          const redirectUri = `${window.location.origin}${window.location.pathname}`;
          request = bankApi.initiateBankConnection("stitch", {
            redirectUri,
          });
        } else if (type === "plaid") {
          request = bankApi.initiateBankConnection(type);
        }
        try {
          const response = await request;
          if (response.status === 201 || response.status === 200) {
            ((
              {
                stitch: () => this.handleStitchInitiationResponse(response),
                plaid: () => this.handlePlaidInitiationResponse(response),
              }[type] || (() => {})
            )());
          } else {
            this.setLoading(false);
            this.country = "";
            this.showToast({
              description: response.data.message,
              display: true,
              type: "error",
            });
          }
        } catch (error) {
          this.setLoading(false);
          this.country = "";
          this.showToast({
            description: "We could not initiate this bank connection.",
            display: true,
            type: "error",
          });
        }
      },

      setupMono(country) {
        // eslint-disable-next-line prefer-const
        let self = this;
        let key = null;
        switch (country) {
          case "ng":
            key = process.env.VUE_APP_MONO_BUSINESS;
            break;
          case "ke":
            key = process.env.VUE_APP_MONO_KE_PERSONAL;
            break;
          case "gh":
            key = process.env.VUE_APP_MONO_GH_BUSINESS;
            break;
        }
        const monoInstance = new MonoConnect({
          onSuccess: function (code) {
            self.setLoading(true);
            code.country = country;
            self.generalSuccessHandler("mono", code);
          },
          key,
        });
        monoInstance.setup();
        return monoInstance;
      },

      handlePlaidInitiationResponse(response) {
        this.plaidSuccess = false;
        this.linkToken = response.data.data.link;
      },
      handleStitchInitiationResponse(response) {
        const { link, state, verifier } = response.data.data;
        setItem(state, verifier);
        window.location = link;
      },
      /**
       * @param {string} type
       * @param {object} data
       */
      async generalSuccessHandler(type, data) {
        this.setLoading(true);
        try {
          const response = await bankApi.finalizeBankConnection(type, data);
          if (response.status === 201 || response.status === 200) {
            this.showToast({
              description: "Bank connection was successful",
              display: true,
              type: "success",
            });
            await this.fetchBankAccounts();
            this.$emit("bankConnected", "selectCountryForm");
            this.setLoading(false);
          } else {
            this.setLoading(false);
            this.showToast({
              description: response.response.data.message,
              display: true,
              type: "error",
            });
          }
        } catch (error) {
          this.country = "";
          this.showToast({
            description: error.data.message,
            display: true,
            type: "error",
          });
          this.setLoading(false);
        }
      },
      onPlaidLoad() {
        console.log("Plaid Loaded");
      },
      onPlaidSuccess(publicToken) {
        if (!this.plaidSuccess) {
          this.linkToken = "";
          this.generalSuccessHandler("plaid", { code: publicToken });
          this.plaidSuccess = true;
        }
      },
      onPlaidExit() {
        this.linkToken = "";
        this.loading = false;
      },
      onPlaidEvent(e) {
        if (e === "HANDOFF") {
          this.$emit("bankConnected", "selectCountryForm");
          this.fetchBankAccounts();
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$text-placeholder: #94A3B8;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$border-hover: #CBD5E1;
$focus-border: #4D84FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;
$chevron: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%2364748B' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");

@mixin field-base {
  width: 100%;
  height: 48px;
  padding: 0 14px;
  background-color: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  font-size: 14px;
  color: $text-primary;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

@mixin field-states {
  &::placeholder {
    color: $text-placeholder;
  }

  &:hover:not(:disabled) {
    border-color: $border-hover;
  }

  &:focus {
    border-color: $focus-border;
    box-shadow: 0 0 0 3px rgba(77, 132, 255, 0.15);
  }
}

@mixin field {
  @include field-base;
  @include field-states;
}

@mixin select-field {
  @include field-base;
  padding-right: 40px;
  appearance: none;
  cursor: pointer;
  background-image: $chevron;
  background-repeat: no-repeat;
  background-position: right 14px center;
  @include field-states;
}

.add-bank {
  &__title {
    margin: 0 0 4px;
    font-size: 16px;
    font-weight: 600;
    color: $text-primary;
  }

  &__desc {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: $text-muted;
  }

  &__label,
  :deep(form label) {
    display: block;
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: 500;
    color: $text-secondary;
  }

  &__label {
    margin-top: 20px;
  }

  &__select,
  :deep(form select) {
    @include select-field;
  }

  :deep(form input[type="text"]:not(.search-input)) {
    @include field;
  }

  :deep(form input.tw-border-error) {
    border-color: #EF4444;
  }

  // SelectInput draws its own chevron against the label + select block, so it sits too high.
  :deep(#select-input > svg) {
    display: none;
  }

  // Account details form under the currency picker.
  :deep(.manual-add-bank),
  :deep(.flutterwave-add-bank) {
    margin-top: 24px;
  }

  :deep(.btn-component) {
    height: 48px;
    background: $btn-primary;
    border-radius: 8px;
    font-size: 15px;
    transition: background-color 0.15s ease;

    &:hover:not(:disabled) {
      background: $btn-primary-hover;
    }

    &:disabled {
      background: $border-color;
      color: $text-muted;
      cursor: not-allowed;
    }
  }
}
</style>
